import { ElectricCar } from '@mui/icons-material'
import { ApplianceConfigEvCharger, ApplianceSubtype } from './Appliance'
import { RelayAppliance } from './RelayAppliance'
import { PortControl } from '../ports/PortControl'
import { Port } from '../../types/models'
import { PortConfig } from '../../types/ports'
import { Box } from '@mui/material'
import { Trans } from 'react-i18next'
import { Text } from '../../components/atoms/Text'
import { EVSEPortControl } from '../ports/EVSEPortControl'
import { Button } from '../../components/atoms/Button'
import { ChangeEvent } from 'react'
import { setCaretPosition } from '../../utils/misc'

export class EvseAppliance extends RelayAppliance<ApplianceConfigEvCharger> {

  get Icon() {
    return ElectricCar
  }

  getDefaultConfig(): ApplianceConfigEvCharger {
    return {
      advanced: false,
      strategy: 'MANUAL',
      plan_by_price: false,
      pv_priority: 2,
      allowed_hours: [22, 7],
      charging_hours: 5,
    }
  }

  isSuitable(ctrl: PortControl<Port, PortConfig>, subtype: ApplianceSubtype): boolean {
    return ctrl.port.type === 'RELAY' || ctrl.port.type === 'EVSE'
  }

  getEvseControl(): EVSEPortControl {
    // Hmm, this is slightly questionable. Can we quarantee that in future it will be second control?
    return this.getControl(0)
  }

  hasAdvanced() {
    return true
  }

  drawSimple(): React.ReactNode {

    const setHour = (index: number, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const hours: [number | string, number | string] = this.getConfig('allowed_hours')
      const value = +event.target.value as number
      if (value < 0) {
        hours[index] = 0
      } else if (value > 24) {
        hours[index] = 24
      } else {
        hours[index] = value
      }
      this.setConfig('allowed_hours', hours)
    }

    const finishHours = (hourInputId: string) => {
      const inputHour = document.getElementById(hourInputId) as HTMLInputElement | HTMLTextAreaElement
      const allowChargingHours = handleCalculateMaxChargingHours()
      const chargingHourInput = document.getElementById('charging_hours_input') as HTMLInputElement | HTMLTextAreaElement
      const inputHourValue = +inputHour.value
      const hours: [number | string, number | string] = this.getConfig('allowed_hours')
      const charging_hours = this.getConfig('charging_hours') as number
      if (charging_hours > allowChargingHours) {
        this.setConfig('charging_hours', allowChargingHours)
        chargingHourInput.value = allowChargingHours + ''
      }

      if (inputHourValue < 0) {
        inputHour.value = 0 + ''
      } else if (inputHourValue > 24) {
        inputHour.value = 24 + ''
      } else {
        this.setConfig('allowed_hours', [
          parseInt(`${hours[0]}` || '0'),
          parseInt(`${hours[1]}` || '0'),
        ])
      }
    }

    if (!this.getConfig('allowed_hours')) {
      this.setConfig('allowed_hours', [22, 7])
      this.setConfig('charging_hours', 5)
    }

    const handleChangeChargeHours = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const [from, to]: [number | string, number | string] = this.getConfig('allowed_hours')
      const allowChargingHours = handleCalculateMaxChargingHours()
      const currentValue = +e.target.value
      if (currentValue < 0 || currentValue > allowChargingHours) {
        this.setConfig('charging_hours', 0)
      } else {
        if (+to > +from) {
          if (currentValue <= allowChargingHours) {
            this.setConfig('charging_hours', currentValue)
          } else {
            this.setConfig('charging_hours', allowChargingHours)
          }
        } else if (+to < +from) {
          if (currentValue <= allowChargingHours) {
            this.setConfig('charging_hours', currentValue)
          } else {
            this.setConfig('charging_hours', allowChargingHours)
          }
        } else if (+to === +from) {
          this.setConfig('charging_hours', currentValue)
        }
      }
    }

    const finishChargingHours = () => {
      const [from, to]: [number | string, number | string] = this.getConfig('allowed_hours')
      const charging_hours = this.getConfig('charging_hours') as number
      const input = document.getElementById('charging_hours_input') as HTMLInputElement | HTMLTextAreaElement
      const allowChargingHours = handleCalculateMaxChargingHours()

      if (charging_hours < 0 || charging_hours > allowChargingHours) { // prevent invalid charging hour if customers don't change the value
        input.value = 0 + ''
      } else {
        if (+to > +from) {
          if (charging_hours <= allowChargingHours) {
            input.value = charging_hours + ''
          } else {
            input.value = allowChargingHours + ''
          }
        } else if (+to < +from) {
          if (charging_hours <= allowChargingHours) {
            input.value = charging_hours + ''
          } else {
            input.value = allowChargingHours + ''
          }
        } else if (+to === +from) {
          input.value = charging_hours + ''
        }
      }
    }

    const handleCalculateMaxChargingHours = () => {
      const [from, to]: [number | string, number | string] = this.getConfig('allowed_hours')
      let gapHours: number = 0

      if (+to > +from) {
        gapHours = +to - +from
      } else if (+to < +from) {
        gapHours = (24 - +from) + (+to - 0)
      } else if (+to === +from) {
        gapHours = 24
      }
      return gapHours
    }

    return (this.getConfig('strategy') !== 'PV_ONLY' &&
    <Box sx={{ mt: 2 }}>
      <Trans>Schedule</Trans>
      <Box sx={{ mt: 2, mb: 3, display: 'grid', gridTemplateColumns: '3fr 2fr 2fr' }}>
        <Box sx={{ pr: 1, textAlign: 'right' }}><Trans>Between</Trans></Box>
        <Text
          id="charging_start_input"
          sx={{ pr: 1 }}
          label=""
          defaultValue={(this.getConfig('allowed_hours') as [number, number])[0]}
          type="number"
          positive
          integer
          min={0}
          max={24}
          onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
          onChange={(e) => setHour(0, e)}
          onKeyDown={(e) => { if (e.code === 'Period') e.preventDefault() }}
          onBlur={() => finishHours('charging_start_input')}
        />
        <Text
          id="charging_end_input"
          sx={{ pr: 0.5 }}
          label=""
          type="number"
          positive
          integer
          min={0}
          max={24}
          defaultValue={(this.getConfig('allowed_hours') as [number, number])[1]}
          onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
          onChange={(e) => setHour(1, e)}
          onBlur={() => finishHours('charging_end_input')}
        />
      </Box>
      <Box sx={{ mt: 2, mb: 3, display: 'grid', gridTemplateColumns: '3fr 2fr 2fr' }}>
        <Box sx={{ pr: 1, textAlign: 'right' }}><Trans>Charge</Trans></Box>
        <Text
            id="charging_hours_input"
            sx={{ pr: 1 }}
            min={0}
            max={handleCalculateMaxChargingHours()}
            label=""
            defaultValue={this.getConfig('charging_hours') || ''}
            type="number"
            onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
            onChange={handleChangeChargeHours}
            onBlur={finishChargingHours}
          />
        <Box><Trans>Hours</Trans></Box>
      </Box>
    </Box>)
  }

  widget(manual: boolean, overriding: boolean, onRefresh: () => void): React.ReactNode {

    if (manual || overriding) return <></>

    if (!this.getConfig('allowed_hours')) {
      this.setConfig('allowed_hours', [22, 7])
      this.setConfig('charging_hours', 5)
    }

    const allowedHours: [number, number] = this.getConfig('allowed_hours')
    const chargingHours: number = parseInt(this.getConfig('charging_hours'))

    allowedHours[0] = parseInt(`${allowedHours[0]}`)
    allowedHours[1] = parseInt(`${allowedHours[1]}`)
    const hours = Math.max(0, allowedHours[0] > allowedHours[1] ? (24 - allowedHours[0]) + allowedHours[1] : allowedHours[1] - allowedHours[0])

    const onClick = (change: 1 | -1) => {
      const newChargingHours = chargingHours + change
      if (newChargingHours >= 0 && newChargingHours <= hours) {
        this.setConfig('charging_hours', newChargingHours)
        onRefresh()
        this.saveConfig()
      }
    }

    return (this.getConfig('strategy') !== 'PV_ONLY' &&
      <Box sx={{ mx: 2, mt: 2, textAlign: 'center' }}>
        <Button onClick={() => onClick(-1)} size="small" variant="text" disabled={!chargingHours}>
          &minus;
        </Button>
        <Box sx={{ display: 'inline-block', mx: 2, p: 2, textAlign: 'center', border: '2px solid gray', borderRadius: 3 }}>
          {chargingHours}
        </Box>
        <Box sx={{ display: 'inline-block', mr: 2, ml: -1.5 }}>h</Box>
        <Button onClick={() => onClick(+1)} size="small" variant="text" disabled={chargingHours >= hours}>
          +
        </Button>

        <Box sx={{ textAlign: 'left', mt: 1 }}>
          (<Trans>Between</Trans> {allowedHours[0]} &mdash; {allowedHours[1]})
        </Box>
      </Box>
    )
  }
}
